// Utilities para anti burn-in OLED. Standalone para test sin React.

export interface Shift {
  x: number
  y: number
}

// Devuelve un offset aleatorio dentro de [-max, max]. Usar para pixel shift.
export function randomShift(maxPx = 4): Shift {
  const range = maxPx * 2 + 1
  return {
    x: Math.floor(Math.random() * range) - maxPx,
    y: Math.floor(Math.random() * range) - maxPx,
  }
}

// Hora local Chile (America/Santiago). Útil para dim nocturno.
// Si el browser corre en otra zona, igual queremos lógica anclada a CL.
export function nowHourInChile(date = new Date()): number {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Santiago',
    hour: 'numeric',
    hour12: false,
  })
  // formatToParts es más confiable que parsear el string completo.
  for (const part of fmt.formatToParts(date)) {
    if (part.type === 'hour') return Number(part.value)
  }
  return date.getHours()
}

// Millis UTC del 00:00 hora Chile del día calendario actual. El topbar resetea
// sus contadores ahí. Chile abolió DST en 2024 → fijo UTC-3; igual usamos Intl
// para que si cambia la política futura no haya que tocar este código.
export function startOfDayInChile(now: Date = new Date()): number {
  const ymd = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
  return new Date(`${ymd}T00:00:00-03:00`).getTime()
}

// Millis UTC del 00:00 Chile del LUNES de la semana calendario actual. Base de
// la ventana "semanal". Chile es UTC-3 fijo → restar días completos (86.4M ms)
// desde el 00:00 de hoy conserva la alineación a medianoche Chile.
const DAYS_SINCE_MONDAY: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 }
export function startOfWeekInChile(now: Date = new Date()): number {
  const wd = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Santiago', weekday: 'short' }).format(now)
  return startOfDayInChile(now) - (DAYS_SINCE_MONDAY[wd] ?? 0) * 86_400_000
}

// Millis UTC del 00:00 Chile del día 1 del MES calendario actual. Base de la
// ventana "mensual" (por mes calendario, no rolling 30 días).
export function startOfMonthInChile(now: Date = new Date()): number {
  const ym = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
  }).format(now)
  return new Date(`${ym}-01T00:00:00-03:00`).getTime()
}

// Etiqueta legible del día actual en zona Chile ("vie 28 may"). Para el
// header del topbar — comunica al operador qué día está mirando.
export function todayLabelChile(now: Date = new Date()): string {
  const label = new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(now)
  // Algunos ICUs devuelven "vie., 28 may" — limpiamos puntos sobrantes.
  return label.replace(/\./g, '')
}

// Dim activo entre [nightStart, dayStart). Default: 22 → 08.
export function shouldDim(
  hour: number,
  nightStart = 22,
  dayStart = 8,
): boolean {
  if (nightStart < dayStart) {
    return hour >= nightStart && hour < dayStart
  }
  // wrap medianoche
  return hour >= nightStart || hour < dayStart
}

// Tier de viewport para decidir densidad de info. NO usar exact 3840 — algunos
// browsers reportan 2560/1920 según device pixel ratio.
export function viewportTier(width: number): 'hd' | '4k' {
  return width >= 2560 ? '4k' : 'hd'
}
